import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Footer from '../General/Footer';
import { figtree } from './fonts';
import LandingNav from './LandingNav';
import Hero from './Hero';
import Marquee from './Marquee';
import Itinerary from './Itinerary';
import Comparison from './Comparison';
import Pricing from './Pricing';
import Faq from './Faq';
import FinalCta from './FinalCta';

// The auth dialogs pull in the MUI dialog stack — keep them out of the
// landing bundle and only load them once a CTA is clicked.
const JoinDialog = dynamic(() => import('../Header/JoinDialog'), {
  ssr: false,
});
const LoginDialog = dynamic(() => import('../Header/LoginDialog'), {
  ssr: false,
});
const PasswordResetDialog = dynamic(
  () => import('../Header/PasswordResetDialog'),
  { ssr: false },
);

// Logged-out landing page. Logged-in users never see this route —
// middleware.js rewrites / to /featured for them.
export default function LandingPage({ featuredPosts, userCount }) {
  const [loginOpen, setLoginOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  // False until the first CTA click — the dialog chunks aren't even
  // requested before that. Stays true afterwards so close animations and
  // the join/login/reset switching keep working.
  const [dialogsReady, setDialogsReady] = useState(false);

  // Paint the document bg paper-colored while the landing page is mounted
  // (rule in styles/style.css), so no gray shows below the footer.
  useEffect(() => {
    document.body.classList.add('tf-landing');
    return () => document.body.classList.remove('tf-landing');
  }, []);

  const openJoin = () => {
    setDialogsReady(true);
    setJoinOpen(true);
  };
  const openLogin = () => {
    setDialogsReady(true);
    setLoginOpen(true);
  };
  const switchDialog = () => {
    setJoinOpen(prev => !prev);
    setLoginOpen(prev => !prev);
  };
  const loginResetSwitch = () => {
    setResetOpen(prev => !prev);
    setLoginOpen(prev => !prev);
  };
  const handleJoinClose = (_, reason) => {
    if (reason !== 'backdropClick') setJoinOpen(false);
  };

  return (
    <div
      className={`${figtree.className} tw-min-h-screen tw-bg-[#FAF8F1] tw-text-[#1D2D28] dark:tw-bg-[#0E1A16] dark:tw-text-[#EBE6D9] tw-antialiased`}
    >
      <style jsx global>{`
        html {
          scroll-behavior: smooth;
        }
      `}</style>

      <LandingNav onJoin={openJoin} onLogin={openLogin} />

      <main>
        <Hero onJoin={openJoin} userCount={userCount} />
        <Marquee />
        <Itinerary featuredPosts={featuredPosts} />
        <Comparison onJoin={openJoin} />
        <Pricing onJoin={openJoin} />
        <Faq />
        <FinalCta onJoin={openJoin} />
      </main>

      <Footer />

      {dialogsReady && (
        <>
          <LoginDialog
            switch={switchDialog}
            handleLoginResetSwitch={loginResetSwitch}
            open={loginOpen}
            handleClose={() => setLoginOpen(false)}
          />
          <JoinDialog
            switch={switchDialog}
            open={joinOpen}
            handleClose={handleJoinClose}
          />
          <PasswordResetDialog
            switch={loginResetSwitch}
            open={resetOpen}
            handleClose={() => setResetOpen(false)}
          />
        </>
      )}
    </div>
  );
}
